import React from "react";
import type { ChatMessage } from "../types";
import ChatEvaluation from "../../UCChatEvaluation";
import UCEvaluationCompleted from "../../UCEvaluationCompleted";

// 时间显示间隔阈值（秒），超过此时间间隔才显示时间戳
const TIMESTAMP_INTERVAL_THRESHOLD = 30; // 30秒（测试用，可根据实际需求调整）

/** V2 消息气泡内时间（HH:mm） */
export const formatMessageTime = (timestamp: number): string =>
  new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export const createV2MessageTimeFooter = (timestamp: number): React.ReactNode => (
  <div className="uchat-v2-message-time">{formatMessageTime(timestamp)}</div>
);
/**
 * API 返回的消息项类型
 */
export interface MemoryItem {
  uuid: string;
  type: "USER" | "BOT" | "HUMAN" | "PROLOGUE" | "EVALUATION" | "REMINDER";
  content: string;
  contentType?: "TEXT" | "JSON" | "IMAGE" | "VIDEO" | "DOCUMENT";
  modifyTime: number;
  createTime?: number;
  [key: string]: any;
}

/**
 * 评价数据接口
 */
interface EvaluationData {
  rating: number;
  feedback?: string;
}

/**
 * 判断是否应该显示时间戳
 * @param currentTimestamp 当前消息的时间戳（秒）
 * @param previousTimestamp 上一条消息的时间戳（秒），如果为 undefined 表示第一条消息
 * @returns 是否应该显示时间戳
 */
export const shouldShowTimestamp = (
  currentTimestamp: number,
  previousTimestamp?: number
): boolean => {
  // 第一条消息总是显示时间
  if (previousTimestamp === undefined) {
    return true;
  }

  // 如果时间间隔超过阈值，显示时间
  const timeDiff = Math.abs(currentTimestamp - previousTimestamp);
  return timeDiff >= TIMESTAMP_INTERVAL_THRESHOLD;
};

/**
 * 消息处理配置接口
 */
export interface MessageProcessorConfig {
  messageRatings: Record<string, number>;
  handleRateMessage: (uuid: string, rating: number) => Promise<void>;
  handleEvaluationSubmit: (rating: number, feedback?: string) => Promise<void>;
  handleEvaluationSkip: () => void;
  intl: any;
  themeColors?: any;
  uiVariant?: "legacy" | "v2";
  createMessageFooter: (options?: {
    uuid?: string;
    rating?: number;
    onRate?: (uuid: string, rating: number) => Promise<void>;
    timestamp?: number;
  }) => React.ReactNode | null;
  createTimestampContent: (timestamp: number) => React.ReactNode;
}

/**
 * 消息处理器类型
 */
type MessageHandler = (
  item: MemoryItem,
  config: MessageProcessorConfig
) => ChatMessage[];

/**
 * 处理用户消息 (USER)
 * 支持文本、图片、视频和文件（DOCUMENT）类型
 */
const handleUserMessage: MessageHandler = (item, config) => {
  if (!item.content) return [];

  // DOCUMENT 类型的消息，content 就是文件URL，直接使用 renderLocalMessage 渲染
  // 其他类型的消息也正常处理，renderLocalMessage 会根据内容类型自动识别
  return [
    {
      key: item.uuid,
      role: "local",
      content: item.content,
      typing: false,
      timestamp: item.modifyTime,
      footer:
        config.uiVariant === "v2"
          ? config.createMessageFooter({ timestamp: item.modifyTime })
          : undefined,
    },
  ];
};

/**
 * 系统提醒（无响应话术），展示为客服侧文本，不提供评分
 */
const handleReminderMessage: MessageHandler = (item, config) => {
  if (!item.content) return [];

  return [
    {
      key: item.uuid,
      role: "ai",
      content: item.content,
      typing: false,
      timestamp: item.modifyTime,
      footer:
        config.uiVariant === "v2"
          ? config.createMessageFooter({ timestamp: item.modifyTime })
          : undefined,
    },
  ];
};

/**
 * 处理 AI 或人工回复消息 (BOT | HUMAN)
 */
const handleBotOrHumanMessage: MessageHandler = (item, config) => {
  if (!item.content) return [];

  const { messageRatings, handleRateMessage, createMessageFooter } = config;
  const orderListContent =
    item.contentType === "JSON" ? buildOrderListContent(item.content, config) : null;

  return [
    {
      key: item.uuid,
      role: "ai",
      content: orderListContent ?? item.content,
      typing: false,
      uuid: item.uuid,
      rating: messageRatings[item.uuid],
      timestamp: item.modifyTime,
      footer:
        config.uiVariant === "v2"
          ? config.createMessageFooter({ timestamp: item.modifyTime })
          : createMessageFooter({
              uuid: item.uuid,
              rating: messageRatings[item.uuid],
              onRate: handleRateMessage,
            }),
    },
  ];
};

/**
 * 订单数据接口
 */
interface OrderData {
  orderNo: string;
  amount?: string;
  status?: string;
  createTime?: number;
  createTimeStr?: string;
  channelName?: string;
  merchantSn?: string;
  channelOrderNo?: string;
  type?: string;
  [key: string]: any;
}

/**
 * 订单列表 JSON 数据接口
 */
interface OrderListData {
  clickable?: boolean;
  orders: OrderData[];
  text?: string;
  type?: string;
}

/**
 * 订单列表组件
 */
const OrderListComponent: React.FC<{
  data: OrderListData;
  themeColors?: any;
  intl: any;
}> = ({ data, themeColors, intl }) => {
  const handleOrderClick = (orderNo: string) => {
    // 触发自定义事件，传递订单号
    const event = new CustomEvent("submit-order", {
      detail: { orderNo },
    });
    window.dispatchEvent(event);
  };

  const { orders, text, clickable } = data;

  const isDark =
    themeColors?.appearance === "dark" ||
    themeColors?.background === "#14172B" ||
    themeColors?.background === "#1e2338" ||
    themeColors?.background === "#000000";
  
  // 主题颜色配置
  const colors = {
    containerBg: isDark ? themeColors?.messageBg || "#1e2338" : themeColors?.background || "#fff",
    cardBg: isDark ? themeColors?.messageBg || "#1e2338" : "#fff",
    cardBgHover: isDark ? themeColors?.hoverBg || "#2a2f47" : themeColors?.hoverBg || "#f0f7ff",
    border: isDark ? themeColors?.border || "#2a2f47" : themeColors?.border || "#e8e8e8",
    borderHover: isDark ? themeColors?.hoverText || "#4a9eff" : themeColors?.evaluationMeta || "#1890ff",
    text: themeColors?.text || (isDark ? "#ffffff" : "#333333"),
    textSecondary: themeColors?.textSecondary || (isDark ? "#9aa3b2" : "#8c8c8c"),
    titleText: themeColors?.text || (isDark ? "#ffffff" : "#262626"),
    successColor: isDark ? "#6bcf6b" : "#52c41a",
    failedColor: "#ff4d4f",
    processingColor: isDark ? "#9aa3b2" : "#8c8c8c",
    arrowColor: isDark ? themeColors?.hoverText || "#4a9eff" : themeColors?.evaluationMeta || "#1890ff",
    shadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.08)",
  };

  // 处理 clickable=false 的情况：显示 text + "无订单"提示
  if (clickable === false) {
    return (
      <div
        style={{
          padding: "16px",
          borderRadius: "12px",
          backgroundColor: colors.containerBg,
          boxShadow: colors.shadow,
          border: `1px solid ${colors.border}`,
        }}
      >
        {text && (
          <div
            style={{
              marginBottom: "12px",
              fontSize: "16px",
              fontWeight: 600,
              color: colors.titleText,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "4px",
                height: "16px",
                backgroundColor: colors.arrowColor,
                borderRadius: "2px",
              }}
            />
            {text}
          </div>
        )}
        <div
          style={{
            fontSize: "14px",
            color: colors.textSecondary,
            lineHeight: "1.6",
          }}
        >
          {intl.formatMessage(
            { id: "order.list.empty" },
            { defaultMessage: "无订单" }
          )}
        </div>
      </div>
    );
  }

  // 如果没有订单列表，返回 null
  if (!orders || orders.length === 0) {
    return null;
  }

  const showOrderIndex = orders.length > 1;

  // 格式化时间显示
  const formatTime = (order: OrderData) => {
    if (order.createTimeStr) {
      return order.createTimeStr;
    }
    if (order.createTime) {
      const date = new Date(order.createTime * 1000);
      // 使用国际化格式化时间，根据当前语言环境
      const locale = intl?.locale || "zh-CN";
      return date.toLocaleString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return null;
  };

  // 获取状态显示文本和颜色
  const getStatusInfo = (status?: string) => {
    if (!status) return null;
    const statusMap: Record<string, { text: string; color: string }> = {
      success: {
        text: intl.formatMessage(
          { id: "order.status.success" },
          { defaultMessage: "成功" }
        ),
        color: colors.successColor,
      },
      failed: {
        text: intl.formatMessage(
          { id: "order.status.failed" },
          { defaultMessage: "失败" }
        ),
        color: colors.failedColor,
      },
      processing: {
        text: intl.formatMessage(
          { id: "order.status.processing" },
          { defaultMessage: "处理中" }
        ),
        color: colors.processingColor,
      },
    };
    return statusMap[status] || { text: status, color: colors.textSecondary };
  };

  return (
    <div
      style={{
        padding: "16px",
        borderRadius: "12px",
        backgroundColor: colors.containerBg,
        boxShadow: colors.shadow,
        border: `1px solid ${colors.border}`,
      }}
    >
      {text && (
        <div
          style={{
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: 600,
            color: colors.titleText,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: "4px",
              height: "16px",
              backgroundColor: colors.arrowColor,
              borderRadius: "2px",
            }}
          />
          {text}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {orders.map((order, index) => {
          const statusInfo = getStatusInfo(order.status);
          const timeStr = formatTime(order);

          return (
            <div
              key={order.orderNo || index}
              onClick={() => handleOrderClick(order.orderNo)}
              style={{
                padding: "16px",
                borderRadius: "10px",
                backgroundColor: colors.cardBg,
                border: `1px solid ${colors.border}`,
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "12px",
                boxShadow: isDark ? "0 1px 3px rgba(0, 0, 0, 0.2)" : "0 1px 2px rgba(0, 0, 0, 0.04)",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.cardBgHover;
                e.currentTarget.style.borderColor = colors.borderHover;
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0 4px 12px rgba(0, 0, 0, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.cardBg;
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 1px 3px rgba(0, 0, 0, 0.2)"
                  : "0 1px 2px rgba(0, 0, 0, 0.04)";
              }}
            >
              {showOrderIndex && (
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: isDark
                      ? "rgba(74, 158, 255, 0.15)"
                      : "rgba(24, 144, 255, 0.1)",
                    color: colors.arrowColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                >
                  {index + 1}
                </div>
              )}

              {/* 左侧内容 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 订单号 */}
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: colors.text,
                    marginBottom: "8px",
                    wordBreak: "break-all",
                    lineHeight: "1.4",
                  }}
                >
                  {order.orderNo}
                </div>

                {/* 订单信息行 */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    marginBottom: "8px",
                  }}
                >
                  {order.amount && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: colors.textSecondary,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>
                        {intl.formatMessage(
                          { id: "order.amount.label" },
                          { defaultMessage: "金额" }
                        )}
                        : {order.amount}
                      </span>
                    </div>
                  )}
                  {order.channelName && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: colors.textSecondary,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>
                        {intl.formatMessage(
                          { id: "order.channel.label" },
                          { defaultMessage: "渠道" }
                        )}
                        : {order.channelName}
                      </span>
                    </div>
                  )}
                  {statusInfo && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: statusInfo.color,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontWeight: 500,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: statusInfo.color,
                        }}
                      />
                      <span>{statusInfo.text}</span>
                    </div>
                  )}
                </div>

                {/* 时间显示 */}
                {timeStr && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: colors.textSecondary,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        opacity: 0.7,
                      }}
                    >
                      🕐
                    </span>
                    <span>{timeStr}</span>
                  </div>
                )}
              </div>

              {/* 右侧箭头 */}
              <div
                style={{
                  fontSize: "18px",
                  color: colors.arrowColor,
                  marginLeft: "16px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                →
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const isOrderListData = (data: OrderListData | null | undefined): boolean =>
  !!data &&
  (data.type === "orderList" || (Array.isArray(data.orders) && data.orders.length > 0));

const buildOrderListContent = (
  content: string,
  config: MessageProcessorConfig
): React.ReactNode | null => {
  try {
    const data: OrderListData = JSON.parse(content);
    if (!isOrderListData(data)) {
      return null;
    }
    return (
      <OrderListComponent
        data={data}
        themeColors={config.themeColors}
        intl={config.intl}
      />
    );
  } catch {
    return null;
  }
};

/**
 * 处理开场白消息 (PROLOGUE)
 */
const handlePrologueMessage: MessageHandler = (item, config) => {
  if (!item.content) return [];

  const v2Footer =
    config.uiVariant === "v2"
      ? config.createMessageFooter({ timestamp: item.modifyTime })
      : undefined;

  // 处理 contentType=JSON 的情况
  if (item.contentType === "JSON") {
    const orderListContent = buildOrderListContent(item.content, config);
    if (orderListContent) {
      return [
        {
          key: item.uuid,
          role: "ai",
          content: orderListContent,
          typing: false,
          timestamp: item.modifyTime,
          footer: v2Footer,
        },
      ];
    }
    console.warn("Failed to parse PROLOGUE JSON content:", item.content);
    return [
      {
        key: item.uuid,
        role: "ai",
        content: item.content,
        typing: false,
        timestamp: item.modifyTime,
        footer: v2Footer,
      },
    ];
  }

  // 默认处理：普通文本内容
  return [
    {
      key: item.uuid,
      role: "ai",
      content: item.content,
      typing: false,
      timestamp: item.modifyTime,
      footer: v2Footer,
    },
  ];
};

/**
 * 处理评价消息 (EVALUATION)
 * 根据 contentType 进行不同的处理：
 * - contentType === "JSON" 表示已评价，只显示已完成组件（不显示提示消息）
 * - contentType === "TEXT" 表示未评价，显示提示消息和评价组件
 */
const handleEvaluationMessage: MessageHandler = (item, config) => {
  if (config.uiVariant === "v2") {
    return [];
  }

  const {
    intl,
    handleEvaluationSubmit,
    handleEvaluationSkip,
    themeColors,
    createMessageFooter,
  } = config;

  // 根据 contentType 判断评价状态
  if (item.contentType === "JSON") {
    // 已评价 - 解析 JSON 并显示已完成提示消息和评价结果
    let evaluationData: EvaluationData | null = null;
    try {
      const data = JSON.parse(item.content);
      if (data && typeof data.rating === "number") {
        evaluationData = data;
      }
    } catch (e) {
      console.warn("Failed to parse evaluation content:", item.content);
      return [];
    }

    if (!evaluationData) return [];

    // 评价完成后的提示消息（与未评价时的提示消息不同）
    const completedPromptMessage: ChatMessage = {
      key: `evaluation-prompt-${item.uuid}`,
      role: "ai",
      content: intl.formatMessage(
        { id: "evaluation.completed.prompt.message" },
        { defaultMessage: "感谢您的评价！您的反馈对我们非常重要。" }
      ),
      typing: false,
      timestamp: item.modifyTime,
      footer: createMessageFooter(),
    };

    return [
      completedPromptMessage,
      {
        key: `evaluation-component-${item.uuid}`,
        role: "system",
        content: (
          <UCEvaluationCompleted
            rating={evaluationData.rating}
            feedback={evaluationData.feedback}
          />
        ),
        messageType: "evaluation",
      },
    ];
  } else if (item.contentType === "TEXT") {
    // 未评价 - 显示提示消息和评价组件
    const promptMessage: ChatMessage = {
      key: `evaluation-prompt-${item.uuid}`,
      role: "ai",
      content: intl.formatMessage({ id: "evaluation.prompt.message" }),
      typing: false,
      timestamp: item.modifyTime,
      footer: createMessageFooter(),
    };

    return [
      promptMessage,
      {
        key: `evaluation-component-${item.uuid}`,
        role: "system",
        content: (
          <ChatEvaluation
            onSubmit={handleEvaluationSubmit}
            onSkip={handleEvaluationSkip}
            themeColors={themeColors}
          />
        ),
        messageType: "evaluation",
      },
    ];
  }

  // 未知的 contentType，返回空数组
  return [];
};

/**
 * 消息类型处理器映射表
 * 方便后续扩展新的消息类型
 */
const messageHandlers: Record<MemoryItem["type"], MessageHandler> = {
  USER: handleUserMessage,
  BOT: handleBotOrHumanMessage,
  HUMAN: handleBotOrHumanMessage,
  REMINDER: handleReminderMessage,
  PROLOGUE: handlePrologueMessage,
  EVALUATION: handleEvaluationMessage,
};

/**
 * 处理单个消息项
 */
const transformMessageItem = (
  item: MemoryItem,
  config: MessageProcessorConfig
): ChatMessage[] => {
  const handler = messageHandlers[item.type];
  if (handler) {
    return handler(item, config);
  }

  // 未知的消息类型，返回空数组
  console.warn(`Unknown message type: ${item.type}`);
  return [];
};

/**
 * 将 API 返回的消息数据转换为 ChatMessage 数组
 * @param items API 返回的消息数据数组
 * @param config 消息处理配置
 * @returns ChatMessage 数组
 */
export const transformMessages = (
  items: MemoryItem[],
  config: MessageProcessorConfig
): ChatMessage[] => {
  return items.flatMap((item) => transformMessageItem(item, config));
};

/**
 * 在消息列表中插入时间戳消息
 * @param messages 原始消息列表
 * @param createTimestampContent 创建时间戳内容的函数
 * @returns 处理后的消息列表（包含时间戳消息）
 */
export const insertTimestamps = (
  messages: ChatMessage[],
  createTimestampContent: (timestamp: number) => React.ReactNode,
  uiVariant?: "legacy" | "v2"
): ChatMessage[] => {
  if (uiVariant === "v2") {
    return messages;
  }

  const processedMessages: ChatMessage[] = [];
  let lastTimestamp: number | undefined;

  messages.forEach((msg, index) => {
    // 如果消息没有时间戳，直接添加（如 PROLOGUE、system 类型消息）
    if (!msg.timestamp) {
      processedMessages.push(msg);
      return;
    }

    // 判断是否需要显示时间戳
    const shouldShow = shouldShowTimestamp(msg.timestamp, lastTimestamp);

    // 如果需要显示时间戳，先插入时间戳消息
    if (shouldShow) {
      processedMessages.push({
        key: `timestamp-${msg.timestamp}-${index}`,
        role: "system",
        content: createTimestampContent(msg.timestamp),
        messageType: "text",
      });
      lastTimestamp = msg.timestamp;
    }

    // 然后添加原消息
    processedMessages.push(msg);
  });

  return processedMessages;
};
